import React, { useState } from 'react';
import { TrashIcon, PencilIcon } from '@heroicons/react/24/outline';
import { useAuthContext } from '../../../hooks/useAuthContext';
import { isCurrentUserAdmin } from '../../../utils/adminUtils';
import DeletionButton from './DeletionButton';
import DeletionConfirmModal from './DeletionConfirmModal';

/**
 * DeletionControls component for handling content deletion and editing
 * Shows appropriate controls based on user permissions
 */
const DeletionControls = ({
  item,
  type, // 'question' or 'reply'
  onDelete,
  onEdit, // Optional edit callback
  className = ''
}) => {
  const { user } = useAuthContext();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminCheckLoading, setAdminCheckLoading] = useState(true);

  // Check admin status on mount
  React.useEffect(() => {
    const checkAdminStatus = async () => {
      if (user?.uid) {
        try {
          const adminStatus = await isCurrentUserAdmin(user);
          setIsAdmin(adminStatus);
        } catch (error) {
          console.error('Error checking admin status:', error);
          setIsAdmin(false);
        }
      }
      setAdminCheckLoading(false);
    };

    checkAdminStatus();
  }, [user]);

  // Determine permissions
  const isAuthor = user?.uid === item?.authorId;
  const canDelete = isAdmin || isAuthor;

  // Don't show controls if user can't delete or content is already deleted
  if (!user || !canDelete || item?.status === 'deleted' || adminCheckLoading) {
    return null;
  }

  const handleDeleteClick = () => {
    setShowConfirmModal(true);
  };

  const handleConfirmDelete = async (reason = '') => {
    if (!onDelete) {
      console.error('onDelete callback not provided');
      return;
    }

    setIsDeleting(true);
    
    try {
      await onDelete(item.id, user.uid, reason);
      setShowConfirmModal(false);
    } catch (error) {
      console.error('Error deleting content:', error);
      // Keep modal open on error so user can retry
    } finally {
      setIsDeleting(false);
    }
  };

  const handleModalClose = () => {
    if (!isDeleting) {
      setShowConfirmModal(false);
    }
  };

  const handleEditClick = () => {
    if (onEdit) {
      onEdit(item.id);
    }
  };

  return (
    <>
      <div className={`flex items-center space-x-2 ${className}`}>
        {/* Edit button - only for authors */}
        {isAuthor && onEdit && (
          <button
            onClick={handleEditClick}
            disabled={isDeleting}
            className="inline-flex items-center space-x-1 px-2 py-1 text-xs font-medium 
                       text-gray-600 hover:text-gray-700 bg-gray-50 hover:bg-gray-100
                       dark:text-gray-400 dark:bg-gray-800 dark:hover:bg-gray-700
                       border border-gray-200 dark:border-gray-600 rounded
                       transition-colors duration-200 focus:outline-none focus:ring-2 
                       focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
            title="Edit your content"
            aria-label="Edit your content"
          >
            <PencilIcon className="w-3 h-3" />
            <span>Edit</span>
          </button>
        )}

        {/* Delete button */}
        <DeletionButton
          isAdmin={isAdmin && !isAuthor}
          onClick={handleDeleteClick}
          disabled={isDeleting}
          size="sm"
        />
      </div>

      <DeletionConfirmModal
        isOpen={showConfirmModal}
        onClose={handleModalClose}
        onConfirm={handleConfirmDelete}
        isAdmin={isAdmin && !isAuthor}
        contentType={type}
        contentTitle={item?.title || 'this content'}
        isLoading={isDeleting}
      />
    </>
  );
};

export default DeletionControls;